import { useAuthenticator } from "@aws-amplify/ui-react";
import { useState, useEffect } from "react";
import {
    deleteContent,
    fetchContent,
    updateContent,
    createContent,
    getUserDetails,
} from "../../Utils/functions";
import {
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    TextField,
} from "@mui/material";
import "./HelpCard.css";

export const HelpCard = () => {
    const { user } = useAuthenticator((context) => [context.user]);
    const [helpContent, setHelpContent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMedRPTAdmin, setIsMedRPTAdmin] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editingHelpId, setEditingHelpId] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    const [newHelpContent, setNewHelpContent] = useState({
        question: "",
        answer: "",
        isActive: true,
    });
    const [showAddForm, setShowAddForm] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [helpToDelete, setHelpToDelete] = useState(null);

    useEffect(() => {
        const checkAdminStatus = async () => {
            if (user && user.userId) {
                try {
                    const userDetails = await getUserDetails();
                    setIsMedRPTAdmin(userDetails?.user?.isMedRPTAdmin || false);
                } catch (err) {
                    console.error("Error checking admin status:", err);
                    setIsMedRPTAdmin(false);
                }
            }
        };
        checkAdminStatus();
    }, [user]);

    useEffect(() => {
        const fetchHelp = async () => {
            try {
                setLoading(true);
                const data = await fetchContent("HELP");
                if (data && Array.isArray(data)) {
                    setHelpContent(data);
                } else {
                    setHelpContent([]);
                }
                setError(null);
            } catch (err) {
                console.error("Failed to fetch Help content", err);
                setError(null);
                setHelpContent([]);
            } finally {
                setLoading(false);
            }
        };
        fetchHelp();
    }, []);

    const handleToggleActive = async (index, e) => {
        e.stopPropagation();
        const help = helpContent[index];
        const currentActiveStatus = help.isActive !== false;
        const updatedActiveStatus = !currentActiveStatus;

        const updatedContent = [...helpContent];
        updatedContent[index] = { ...updatedContent[index], isActive: updatedActiveStatus };
        setHelpContent(updatedContent);
        try {
            setSaving(true);
            await updateContent(help.question, help.answer, updatedActiveStatus, help.id, "HELP");
            setSnackbar({
                open: true,
                message: `Help content ${updatedActiveStatus ? "activated" : "deactivated"} successfully`,
                severity: "success",
            });
        } catch (err) {
            const originalHelpContent = [...helpContent];
            originalHelpContent[index] = {
                ...originalHelpContent[index],
                isActive: currentActiveStatus,
            };
            setHelpContent(originalHelpContent);
            setSnackbar({
                open: true,
                message: "Failed to update Help content status: " + err.message,
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (index) => {
        const help = helpContent[index];
        setNewHelpContent({
            question: help.question,
            answer: help.answer,
            isActive: help.isActive !== false,
        });
        setEditingHelpId(help.id);
        setShowAddForm(true);
    };

    const handleDeleteClick = (index) => {
        setHelpToDelete(index);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (helpToDelete === null) return;
        const help = helpContent[helpToDelete];
        try {
            setSaving(true);
            await deleteContent(help.id);
            setHelpContent(helpContent.filter((_, i) => i !== helpToDelete));
            setDeleteDialogOpen(false);
            setHelpToDelete(null);
            setSnackbar({
                open: true,
                message: "Help content deleted successfully",
                severity: "success",
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: "Failed to delete Help content: " + err.message,
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setHelpToDelete(null);
    };

    const handleAdd = async () => {
        if (!newHelpContent.question.trim() || !newHelpContent.answer.trim()) {
            setSnackbar({
                open: true,
                message: "Please fill in both question and answer",
                severity: "warning",
            });
            return;
        }

        const isEditing = editingHelpId !== null;

        try {
            setSaving(true);
            setSnackbar({
                open: true,
                message: isEditing ? "Updating Help content..." : "Adding new Help content...",
                severity: "info",
            });

            if (isEditing) {
                await updateContent(
                    newHelpContent.question,
                    newHelpContent.answer,
                    newHelpContent.isActive,
                    editingHelpId,
                    "HELP"
                );
                const updatedHelpContent = helpContent.map((item) =>
                    item.id === editingHelpId
                        ? {
                            ...item,
                            question: newHelpContent.question,
                            answer: newHelpContent.answer,
                            isActive: newHelpContent.isActive,
                        }
                        : item
                );
                setHelpContent(updatedHelpContent);
            } else {
                await createContent(
                    newHelpContent.question,
                    newHelpContent.answer,
                    newHelpContent.isActive,
                    "HELP"
                );
                const data = await fetchContent("HELP");
                if (data && Array.isArray(data)) {
                    setHelpContent(data);
                }
            }

            setNewHelpContent({ question: "", answer: "", isActive: true });
            setEditingHelpId(null);
            setShowAddForm(false);
            setSnackbar({
                open: true,
                message: isEditing
                    ? "Help content updated successfully!"
                    : "Help content added successfully!",
                severity: "success",
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: isEditing
                    ? "Failed to update Help content: " + err.message
                    : "Failed to create Help content: " + err.message,
                severity: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    return (
        <>
            <div className="help-card-content-wrapper">
                <div className="help-card-content">
                    {loading ? (
                        <div className="help-card-loading">Loading...</div>
                    ) : error ? (
                        <div className="help-card-error">{error}</div>
                    ) : (
                        helpContent.map((help, index) => (
                            <div key={help.id || index} className="help-card-item">
                                <div className="help-card-question-row">
                                    <span
                                        className="help-card-question-text"
                                        dangerouslySetInnerHTML={{ __html: help.question }}
                                    />

                                </div>
                                <div
                                    className="help-card-answer"
                                    dangerouslySetInnerHTML={{ __html: help.answer }}
                                />
                                {isMedRPTAdmin && (
                                    <div className="help-card-admin-actions">
                                        <button
                                            type="button"
                                            className="help-card-edit-btn"
                                            onClick={() => handleEdit(helpContent.findIndex((h) => h.id === help.id))}
                                        >
                                            Edit
                                        </button>
                                        <div
                                            className="help-card-active-toggle"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <label className="help-card-toggle-switch">
                                                <input
                                                    type="checkbox"
                                                    checked={Boolean(help.isActive !== false)}
                                                    onChange={(e) =>
                                                        handleToggleActive(
                                                            helpContent.findIndex((h) => h.id === help.id),
                                                            e
                                                        )
                                                    }
                                                    disabled={saving}
                                                />
                                                <span className="help-card-toggle-slider" />
                                            </label>
                                            <span className="help-card-toggle-label">
                                                {help.isActive !== false ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            className="help-card-delete-btn"
                                            onClick={() =>
                                                handleDeleteClick(helpContent.findIndex((h) => h.id === help.id))
                                            }
                                            disabled={saving}
                                        >
                                            Delete
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                    {isMedRPTAdmin && (
                        <button
                            type="button"
                            className="help-card-add-button"
                            onClick={() => {
                                setNewHelpContent({ question: "", answer: "", isActive: true });
                                setEditingHelpId(null);
                                setShowAddForm(true);
                            }}
                        >
                            + Add Help
                        </button>
                    )}
                </div>
            </div>

            {showAddForm && (
                <Dialog
                    open={showAddForm}
                    onClose={() => {
                        setShowAddForm(false);
                        setNewHelpContent({ question: "", answer: "", isActive: true });
                        setEditingHelpId(null);
                    }}
                    aria-labelledby="add-help-dialog-title"
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle id="add-help-dialog-title">
                        {editingHelpId ? "Edit Help" : "Add New Help"}
                    </DialogTitle>
                    <DialogContent
                        sx={{
                            minWidth: "500px",
                            padding: "24px",
                            "@media (max-width: 768px)": { minWidth: "auto", padding: "16px" },
                        }}
                    >
                        <TextField
                            value={newHelpContent.question}
                            onChange={(e) =>
                                setNewHelpContent({ ...newHelpContent, question: e.target.value })
                            }
                            placeholder="Enter question..."
                            fullWidth
                            sx={{ marginBottom: "20px" }}
                        />
                        <TextField
                            value={newHelpContent.answer}
                            onChange={(e) =>
                                setNewHelpContent({ ...newHelpContent, answer: e.target.value })
                            }
                            placeholder="Enter answer..."
                            multiline
                            rows={8}
                            fullWidth
                            sx={{ marginBottom: "20px" }}
                        />
                        {isMedRPTAdmin && (
                            <div className="help-card-active-toggle" style={{ marginTop: "10px" }}>
                                <label className="help-card-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={newHelpContent.isActive !== false}
                                        onChange={(e) =>
                                            setNewHelpContent({ ...newHelpContent, isActive: e.target.checked })
                                        }
                                        disabled={saving}
                                    />
                                    <span className="help-card-toggle-slider" />
                                </label>
                                <span className="help-card-toggle-label">
                                    {newHelpContent.isActive !== false ? "Active" : "Inactive"}
                                </span>
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setShowAddForm(false);
                                setNewHelpContent({ question: "", answer: "", isActive: true });
                                setEditingHelpId(null);
                            }}
                            sx={{ color: "#49994C" }}
                            disabled={saving}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleAdd}
                            variant="contained"
                            sx={{
                                backgroundColor: "#49994C",
                                "&:hover": { backgroundColor: "#3d7d40" },
                            }}
                            disabled={saving}
                        >
                            {saving
                                ? editingHelpId
                                    ? "Updating..."
                                    : "Adding..."
                                : editingHelpId
                                    ? "Update Help"
                                    : "Add Help"}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                aria-labelledby="delete-help-dialog-title"
            >
                <DialogTitle id="delete-help-dialog-title">Delete Help</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this help item? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} sx={{ color: "#49994C" }} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        color="error"
                        variant="contained"
                        disabled={saving}
                    >
                        {saving ? "Deleting..." : "Delete"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};
